import { Loader } from '@10xLabs/react-web/components/Loader'
import { Map as GoogleMap, useMap } from '@vis.gl/react-google-maps'
import { isEmpty } from 'lodash'
import { ReactElement, useEffect, useMemo, useRef, useState } from 'react'
import { FormattedMessage } from 'react-intl'
import styled from 'styled-components'

import EmptyState from 'components/v2/EmptyState'
import Floating from 'components/Floating'
import { DatabaseIcon } from 'components/Icons/DatabaseIcon'
import { TuneIcon } from 'components/Icons/TuneIcon'
import { Polyline } from 'components/GoogleMaps/Polyline'
import { DEFAULT_MAP_STYLE_ID } from 'config/constants'
import { googleMapsConfig } from 'config/google-maps'
import { CreateDatasetModal } from 'features/RoutingProjects/components/CreateDatasetModal'
import { IRoutingProjectPlanModel, TDatasetLocationsGeojson } from 'features/RoutingProjects/types'
import { ClusteredMarkers } from 'features/RoutingProjects/components/RoutingProjectMap/ClusteredMarkers'
import { CreateConstraintsForm } from 'features/RoutingProjects/components/CreateConstraintsForm'
import { SelectMapStylePanel } from 'features/RoutingProjects/components/RoutingProjectMap/SelectMapStylePanel'
import { mapDatasetLocationsToGeojson, mapOptimizedRouteToGeojson } from 'features/RoutingProjects/utils'
import { useGoogleMapsScript } from 'hooks/google-maps'
import { adjustMapBounds } from 'utils/google-maps'

const Container = styled.div`
  position: relative;
  height: 100%;
  min-height: 300px;
`

const DEFAULT_MAP_TYPE = 'roadmap'

interface IRoutingProjectViewMapProps {
  routingProjectId: string
  planModel: IRoutingProjectPlanModel | null
  planLoading: boolean
  visibleRoutesIds: string[]
}

export const RoutingProjectViewMap = ({
  routingProjectId,
  planModel,
  planLoading,
  visibleRoutesIds
}: IRoutingProjectViewMapProps): ReactElement => {
  const map = useMap()
  const mapInstanceRef = useRef<google.maps.Map | null>(null)
  const [mapStyle, setMapStyle] = useState({
    mapId: DEFAULT_MAP_STYLE_ID,
    mapType: DEFAULT_MAP_TYPE
  })
  const [stopInfoWindowOpenedCount, setStopInfoWindowOpenedCount] = useState(0)
  const [isOpenCreateDatasetModal, setIsOpenCreateDatasetModal] = useState(false)
  const [isOpenCreateConstraintsPanel, setIsOpenCreateConstraintsPanel] = useState(false)
  const { isLoaded: isGoogleMapsLoaded, geometryLib } = useGoogleMapsScript()
  const { defaultCenter, defaultZoom } = googleMapsConfig
  useEffect(() => {
    if (planLoading) {
      mapInstanceRef.current = null
    }
  }, [planLoading, geometryLib])

  const visibleRoutes = useMemo(() => {
    if (planModel?.optimization?.routes) {
      return planModel?.optimization?.routes.filter((route) => visibleRoutesIds.includes(route.id)) || []
    }
    return []
  }, [planModel, visibleRoutesIds])

  const geojson: TDatasetLocationsGeojson = useMemo(() => {
    if (!isEmpty(planModel?.optimization?.routes)) {
      const constraints = planModel?.constraints || {}
      const skippedLocations = planModel?.optimization?.skippedLocations || []
      const visibleRoutes = planModel?.optimization?.routes.filter((route) => visibleRoutesIds.includes(route.id)) || []
      return mapOptimizedRouteToGeojson(visibleRoutes || [], skippedLocations, constraints)
    } else {
      const datasetLocations = planModel?.dataset?.locations || []

      return mapDatasetLocationsToGeojson(datasetLocations)
    }
  }, [planModel, visibleRoutesIds])

  useEffect(() => {
    if (!map || isEmpty(planModel?.dataset?.locations)) return

    const records =
      planModel?.dataset?.locations?.map((location) => {
        return {
          lat: location.coordinates?.latitude || 0,
          lng: location.coordinates?.longitude || 0
        }
      }) || []
    if (!mapInstanceRef.current) {
      adjustMapBounds(records, map)
    }
    if (map && !mapInstanceRef.current) {
      mapInstanceRef.current = map
    }
  }, [map, planModel, visibleRoutes])

  if (!isGoogleMapsLoaded) {
    return (
      <EmptyState.Container>
        <Loader />
      </EmptyState.Container>
    )
  }

  const openCreateDatasetModal = (): void => {
    setIsOpenCreateDatasetModal(true)
  }

  const toggleCreateConstraintsPanel = (): void => {
    setIsOpenCreateConstraintsPanel(!isOpenCreateConstraintsPanel)
  }

  return (
    <Container>
      <Floating.Container>
        <GoogleMap
          mapId={mapStyle.mapId}
          mapTypeId={mapStyle.mapType}
          defaultZoom={defaultZoom}
          defaultCenter={{
            lat: defaultCenter.LAT,
            lng: defaultCenter.LNG
          }}
          reuseMaps
          {...googleMapsConfig.defaultOptions}
        >
          {visibleRoutes.map((route, routeIndex) => {
            if (isEmpty(route.decodedPolyline)) return null

            const color = route.rgbColor

            return (
              <Polyline
                key={routeIndex}
                path={route.decodedPolyline}
                options={{
                  geodesic: true,
                  strokeColor: color,
                  strokeOpacity: 1,
                  strokeWeight: 3
                }}
              />
            )
          })}
          {geojson && (
            <ClusteredMarkers
              geojson={geojson}
              stopInfoWindowOpenedCount={stopInfoWindowOpenedCount}
              setStopInfoWindowOpenedCount={setStopInfoWindowOpenedCount}
            />
          )}
        </GoogleMap>
        {isOpenCreateConstraintsPanel && (
          <Floating.Content top right overflow>
            <Floating.Panel>
              <Floating.ClosePanelButton onClick={toggleCreateConstraintsPanel} />
              {planModel?.id ? (
                <CreateConstraintsForm
                  planModel={planModel}
                  routingProjectId={routingProjectId}
                  closePanel={toggleCreateConstraintsPanel}
                  planLoading={planLoading}
                />
              ) : (
                <EmptyState.Container>
                  <EmptyState.Description>
                    <FormattedMessage
                      id="routingProjects.create.constraints.selectPlan"
                      defaultMessage="Select a plan to create constraints"
                    />
                  </EmptyState.Description>
                </EmptyState.Container>
              )}
            </Floating.Panel>
          </Floating.Content>
        )}
        <Floating.Content top center>
          <Floating.Toolbar>
            <Floating.ToolbarItem>
              <Floating.ToolbarButton onClick={openCreateDatasetModal}>
                <DatabaseIcon />
              </Floating.ToolbarButton>
            </Floating.ToolbarItem>
            <Floating.ToolbarItem>
              <Floating.ToolbarButton onClick={toggleCreateConstraintsPanel}>
                <TuneIcon />
              </Floating.ToolbarButton>
            </Floating.ToolbarItem>
          </Floating.Toolbar>
        </Floating.Content>
        <SelectMapStylePanel setMapStyle={setMapStyle} />
      </Floating.Container>
      {isOpenCreateDatasetModal && (
        <CreateDatasetModal
          routingProjectId={routingProjectId}
          closeModal={(): void => setIsOpenCreateDatasetModal(false)}
        />
      )}
    </Container>
  )
}
